import { positionKey, START_FEN } from "../chess/positionKey.ts";

export const SESSION_PHASES = [
  "Loading",
  "StudentTurn",
  "Assessing",
  "OpponentTurn",
  "Reviewing",
  "FreePlay",
] as const;

export type SessionPhase = (typeof SESSION_PHASES)[number];

export type SessionSnapshot = {
  sessionId: string;
  turnId: string;
  openingId: string;
  phase: SessionPhase;
  fen: string;
  ply: number;
  positionKey: string;
};

export type SessionIdentity = {
  sessionId: string;
  turnId?: string;
  positionKey?: string;
};

function newOpaqueId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export class SessionMachine {
  sessionId = "";
  turnId = "";
  openingId = "";
  phase: SessionPhase = "Loading";
  fen = START_FEN;
  ply = 0;
  generation = 0;

  get snapshot(): SessionSnapshot {
    return {
      sessionId: this.sessionId,
      turnId: this.turnId,
      openingId: this.openingId,
      phase: this.phase,
      fen: this.fen,
      ply: this.ply,
      positionKey: positionKey(this.fen),
    };
  }

  start(openingId: string, fen = START_FEN): SessionSnapshot {
    this.generation += 1;
    this.sessionId = newOpaqueId();
    this.turnId = newOpaqueId();
    this.openingId = openingId;
    this.fen = fen;
    this.ply = 0;
    this.phase = "StudentTurn";
    return this.snapshot;
  }

  beginStudentMove(): SessionSnapshot {
    this.turnId = newOpaqueId();
    this.phase = "Assessing";
    return this.snapshot;
  }

  notePosition(fen: string, ply: number): SessionSnapshot {
    this.fen = fen;
    this.ply = ply;
    return this.snapshot;
  }

  assessmentReady(): SessionSnapshot {
    if (this.phase === "Assessing") this.phase = "OpponentTurn";
    return this.snapshot;
  }

  opponentReplied(fen: string, ply: number): SessionSnapshot {
    this.fen = fen;
    this.ply = ply;
    if (this.phase === "OpponentTurn" || this.phase === "Assessing") {
      this.phase = "StudentTurn";
    }
    return this.snapshot;
  }

  undo(fen: string, ply: number): SessionSnapshot {
    this.turnId = newOpaqueId();
    this.fen = fen;
    this.ply = ply;
    this.phase = "StudentTurn";
    return this.snapshot;
  }

  complete(): SessionSnapshot {
    this.phase = "Reviewing";
    return this.snapshot;
  }

  continueFreePlay(): SessionSnapshot {
    this.phase = "FreePlay";
    return this.snapshot;
  }

  isCurrent(ids: SessionIdentity): boolean {
    if (!ids.sessionId || ids.sessionId !== this.sessionId) return false;
    if (ids.turnId && ids.turnId !== this.turnId) return false;
    if (ids.positionKey && ids.positionKey !== positionKey(this.fen)) return false;
    return true;
  }
}
